import { TENANT_CONFIG as santaclaraRaw } from '../../tenants/santaclara/config/index.js';
import { TENANT_CONFIG as proverRaw } from '../../tenants/prover/config/index.js';
import { TENANT_CONFIG as leblancRaw } from '../../tenants/leblanc/config/index.js';
import { composeTenantConfig } from '../saas/tenantComposition.js';

const rawTenantConfigs = [santaclaraRaw, proverRaw, leblancRaw];

export const TENANT_CONFIGS = Object.freeze(Object.fromEntries(
  rawTenantConfigs.map((tenant) => [tenant.id, composeTenantConfig(tenant)])
));

export function normalizeTenantId(tenantId) {
  if (tenantId === null || tenantId === undefined) return '';
  return String(tenantId).trim().replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 64);
}

export function strictTenantId(tenantId) {
  if (!tenantId) return null;
  const value = String(tenantId).trim();
  if (!/^[a-zA-Z0-9_-]{1,64}$/.test(value)) return null;
  return value;
}

export function hasTenantId(tenantId) {
  const normalized = normalizeTenantId(tenantId);
  return Boolean(normalized && TENANT_CONFIGS[normalized]);
}

export function getTenantById(tenantId) {
  const normalizedTenantId = normalizeTenantId(tenantId);
  if (!normalizedTenantId) return null;
  return TENANT_CONFIGS[normalizedTenantId] || null;
}

export function requireTenantById(tenantId) {
  const tenant = getTenantById(tenantId);
  if (!tenant) {
    const error = new Error(tenantId ? `Unknown tenant: ${tenantId}` : 'Tenant is required');
    error.code = tenantId ? 'TENANT_NOT_FOUND' : 'TENANT_REQUIRED';
    throw error;
  }
  return tenant;
}

function normalizeHostname(hostname) {
  if (!hostname) return '';
  return String(hostname).trim().toLowerCase().replace(/^www\./, '').replace(/\.$/, '');
}

export function resolveTenantIdFromHostname(hostname) {
  const normalizedHost = normalizeHostname(hostname);
  if (!normalizedHost) return null;

  for (const tenant of Object.values(TENANT_CONFIGS)) {
    const domains = Array.isArray(tenant.domains) ? tenant.domains : [];
    const matches = domains.some((domain) => normalizeHostname(domain) === normalizedHost);
    if (matches) return tenant.id;
  }
  return null;
}

export function listTenantConfigs() {
  return Object.values(TENANT_CONFIGS);
}

export function listTenantSummaries() {
  return listTenantConfigs().map((tenant) => ({
    id: tenant.id,
    name: tenant.name,
    shortName: tenant.shortName,
    vertical: tenant.vertical,
    architecture: tenant.architecture,
    module_count: Object.values(tenant.modules || {}).filter((module) => module.enabled).length,
    channels: Object.entries(tenant.channels || {}).filter(([, channel]) => channel.enabled).map(([key]) => key)
  }));
}
